import { Button } from 'antd';
import { createAsyncSelectors } from '@lib/selectors';
import groupBy from 'lodash/groupBy';
import { SearchOutlined } from '@ant-design/icons';

import { ADMIN_DASHBOARD_COPY, SHARD_CATALOG_GROUP_COPY } from '@sonar-software/sonar-ts-constants';
import { adminActions, ShardSearchEntry, ShardSearchEntryDetails } from '@sonar-software/sonar-admin';
import React, { useCallback, useEffect, useMemo } from 'react';
import Select, { DefaultOptionType } from 'antd/es/select';
import { useAction, useSonarSelector } from '@hooks';

export interface ShardCatalogSearchProps {
	id?: string;
	filtertype?: 'customer' | 'user' | 'org';
	placeholder?: string;
	onDetailsChanged?: (details: ShardSearchEntryDetails | null | undefined) => void;
}

export interface ShardCatalogSearchResult extends DefaultOptionType {
	key: string;
	searchkey: string;
	searchshard: string;
	searchtype: string;
}

function getOption(searchEntry: ShardSearchEntry): ShardCatalogSearchResult {
	return {
		key: `${searchEntry.type}:${searchEntry.key}:${searchEntry.shardName}`,
		label: `${searchEntry.text} - ${searchEntry.shardName}`,
		searchkey: searchEntry.key,
		searchshard: searchEntry.shardName,
		searchtype: searchEntry.type,
		value: searchEntry.text
	};
}

const shardCatalogSearchSelectors = createAsyncSelectors((state) => state.admin.search.shardCatalog.get);
const shardCatalogDetailSelectors = createAsyncSelectors((state) => state.admin.search.shardCatalog.details.get);

export const ShardCatalogSearch: React.FC<ShardCatalogSearchProps> = (props: ShardCatalogSearchProps) => {
	const { filtertype, onDetailsChanged, placeholder } = props;

	const shardCatalogSearchResults = useSonarSelector(shardCatalogSearchSelectors.data);
	const shardCatalogDetails = useSonarSelector(shardCatalogDetailSelectors.data);

	const shardCatalogSearchOptions = useMemo(() => {
		if (!shardCatalogSearchResults) {
			return [];
		}

		const resultsByType = groupBy(shardCatalogSearchResults, 'type');

		const results = Object.keys(resultsByType).map((key) => {
			const typeResults = resultsByType[ key ];

			return {
				label: SHARD_CATALOG_GROUP_COPY[ key ],
				options: typeResults.map((result) => getOption(result))
			} as DefaultOptionType;
		});

		return results;


	}, [ shardCatalogSearchResults ]);

	const getShardCatalogSearch = useAction(adminActions.requestGetAdminSearchShardCatalog);
	const getShardCatalogDetails = useAction(adminActions.requestGetAdminSearchShardCatalogDetails);

	const handleOnSearch = useCallback(
		(query: string) => {
			getShardCatalogSearch({
				query: query,
				type: filtertype
			});
		},
		[ getShardCatalogSearch, filtertype ]
	);

	const handleOnSelect = useCallback(
		(value: string, option: ShardCatalogSearchResult) => {
			getShardCatalogDetails({
				key: option.searchkey,
				shardName: option.searchshard,
				type: option.searchtype
			});
		},
		[ getShardCatalogDetails ]
	);

	useEffect(() => {
		if (onDetailsChanged) {
			onDetailsChanged(shardCatalogDetails);
		}
	}, [ onDetailsChanged, shardCatalogDetails ]);

	return (
		<div
			className='shard-catalog-search'
			id={ props.id }>
			<Select
				allowClear
				filterOption={ false }
				labelInValue={ props.filtertype !== undefined }
				maxLength={ 128 }
				onSearch={ handleOnSearch }
				onSelect={ handleOnSelect }
				options={ shardCatalogSearchOptions }
				placeholder={ placeholder ?? ADMIN_DASHBOARD_COPY.SEARCH_PLACEHOLDER }
				showSearch />
			<Button
				icon={ <SearchOutlined /> }
				type='primary' />
		</div>
	);
};
